import {
	Button,
	Link,
	VStack,
	FormControl,
	Text,
	useBreakpointValue,
	Input,
	Textarea,
	Flex,
	FormErrorMessage,
	Box,
	Tooltip,
} from "@chakra-ui/react";
import { Formik, Field, Form } from "formik";
import * as Yup from 'yup';
import { chakra } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { Switch } from "@chakra-ui/react";
import RecentChecksList from "./RecentChecksList";
import { useEffect } from 'react';

const ethereumAddressRegex = /^0x[a-fA-F0-9]{40}$/;

const validationSchema = Yup.object().shape({
	address: Yup.string()
		.required('Address is required')
		.min(1, 'Address cannot be empty')
		.max(1000, 'Address must be at most 1000 characters')
		.matches(ethereumAddressRegex, 'Invalid Ethereum address'),
});

const multipleValidationSchema = Yup.object().shape({
	addresses: Yup.string()
		.required('Addresses are required')
		.min(1, 'Addresses cannot be empty')
		.test('is-valid-addresses', 'Invalid Ethereum address(es)', (value) => {
			const addresses = value.split('\n').map(addr => addr.trim());
			return addresses.every(addr => ethereumAddressRegex.test(addr));
		})
		.test('max-3-addresses', 'Please, enter only up to 3 addresses due to high server load.', (value) => {
			const addresses = value.split('\n').filter(addr => addr.trim() !== '');
			return addresses.length <= 3;
		}),
});



const CheckRankForm = ({ isLoading, isMultiple, handleSwitchChange, handleSubmit, recentChecks, handleClearRecentChecks, serverError, isUserHasKey, isKeyMintAvailiable }) => {
	const isMobile = useBreakpointValue({ base: true, md: false });
	// Effect to automatically switch to "Multiple" mode if the user has a key
	useEffect(() => {
		if (isUserHasKey && !isMultiple) {
			handleSwitchChange({ target: { checked: true } }); // Simulate switch change
		}
	}, [isUserHasKey, isMultiple, handleSwitchChange]);

	return (
		<Formik
			initialValues={isMultiple ? { addresses: '' } : { address: '' }}
			validationSchema={isMultiple ? multipleValidationSchema : validationSchema}
			onSubmit={handleSubmit}
			validateOnChange={false}
			validateOnBlur={false}
		>
			{({ isSubmitting, errors, touched, values, setFieldValue }) => (
				<Form>
					<Flex justifyContent={'center'}>
						<VStack spacing={4} align="stretch" w={isMobile ? '100%' : "700px"} alignContent={"center"}>
							{!isLoading &&
								<FormControl display="flex" alignItems="center" justifyContent="center">
									<Text mr={2}>Single</Text>
									{!isKeyMintAvailiable ? <Tooltip
										placement='top'
										label={<Text textAlign={"center"}>This feature will be available soon.<br /> Stay tuned for announcements on our social media.</Text>}
										aria-label="Feature coming soon"
										bg="#FEEFDD"
										color={"black"}
									>
										<Box>
											<Switch id="mode-switch" isChecked={isMultiple} onChange={handleSwitchChange} colorScheme="orange" disabled />
										</Box>
									</Tooltip> :
										<Switch id="mode-switch" isChecked={isMultiple} onChange={handleSwitchChange} colorScheme="orange" />
									}

									<Text ml={2}>Multiple</Text>
								</FormControl>
							}
							{isMultiple ? (
								<>
									<Field name="addresses">
										{({ field }) => (
											<FormControl isInvalid={errors.addresses && touched.addresses}>
												<Field
													as={Textarea}
													fontSize={!isMobile ? "1.25rem" : '0.8rem'}
													id={"addresses"}
													name={"addresses"}
													placeholder={"Enter one address per line..."}
													height={isMobile ? '35px' : '60px'}
													minWidth={isMobile ? 'auto' : '700px'}
													width="100%"
													border="1px solid rgb(255, 104, 75)"
													outline="1px solid rgb(255, 104, 75)"
													textAlign="center"
													_focus={{
														borderColor: "rgb(255, 104, 75)",
														outline: "1px solid rgb(255, 104, 75)"
													}}
													sx={{
														'::placeholder': {
															fontSize: !isMobile ? "1.2rem" : '0.8rem',
															color: 'gray.400',
														}
													}}
												/>

												<FormErrorMessage>{errors.addresses}</FormErrorMessage>
												{!errors.addresses && <Box color={"red.500"} mt={2} fontSize={"sm"}>{serverError}</Box>}
											</FormControl>
										)}
									</Field>
								</>
							) : (
								<Field name="address">
									{({ field }) => (
										<FormControl isInvalid={errors.address && touched.address}>
											<Field
												as={Input}
												fontSize={!isMobile ? "1.25rem" : '0.8rem'}
												id={"address"}
												name={"address"}
												placeholder={"Enter your wallet..."}
												height={isMobile ? '35px' : '60px'}
												minWidth={isMobile ? 'auto' : '700px'}
												width="100%"
												border="1px solid rgb(255, 104, 75)"
												outline="1px solid rgb(255, 104, 75)"
												textAlign="center"
												_focus={{
													borderColor: "rgb(255, 104, 75)",
													outline: "1px solid rgb(255, 104, 75)"
												}}
												sx={{
													'::placeholder': {
														fontSize: !isMobile ? "1.2rem" : '0.8rem',
														color: 'gray.400',
													}
												}}
											/>

											<FormErrorMessage>{errors.address}</FormErrorMessage>
											{!errors.address && <Box color={"red.500"} mt={2} fontSize={"sm"}>{serverError}</Box>}
										</FormControl>
									)}
								</Field>
							)}

							<Button
								type="submit"
								isLoading={isSubmitting}
								background="#FF684B"
								color="white"
								_hover={{ background: "#FF5733" }}
								height={!isMobile ? "58px" : "35px"}
								isDisabled={!values.address && !values.addresses}
								size={!isMobile ? 'lg' : 'xs'}
							>
								GET RANK
							</Button>

							<Text align="center">
								Any question?{' '}
								<Link as={RouterLink} to="/faq">
									<chakra.span color="#FF5733" _hover={{ textDecoration: "underline" }}>
										Read FAQ
									</chakra.span>
								</Link>
							</Text>
						</VStack>
					</Flex>
					<RecentChecksList
						isUserHasKey={isUserHasKey}
						recentChecks={recentChecks}
						onSelectAddress={(address) => setFieldValue(isMultiple ? 'addresses' : 'address', address)}
						onSelectAddresses={(addresses) => setFieldValue('addresses', addresses)}
						onClear={handleClearRecentChecks}
					/>
				</Form>
			)}
		</Formik>
	);
};

export default CheckRankForm;
